/* eslint-disable indent */
import axios from "axios";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import ExcelIcon from "../../assets/images/excel.png";
import Spinner from "../../assets/images/Spinner.gif";
import { usersList } from "../../Redux/Actions/Users";
import { ApiUrl } from "../Common/ApiUrl";
import { Constants } from "../Common/Constants";
import DateRangePicker from "../Common/Fields/DateRangePicker";
import ReactPagination from "../Common/Fields/ReactPagination";
import ReactSelect from "../Common/Fields/ReactSelect";
import titleCase from "../Common/Functions/titleCase";
import usePermissions from "../Common/Functions/usePermissions";
import PageHeader from "../Common/PageHeader";
import { RoutesUrl } from "../Common/RoutesUrl";
import TableList from "../Common/UiModel/TableList";


const initDate = {
  startDate: new Date(),
  endDate: new Date(),
  key: "selection",
};

const UsersList = () => {
  const dispatch = useDispatch();
  const { userslist } = useSelector((state) => state.Users);
  const [usersData, setUsersData] = useState([]);
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [selectedGender, setSelectedGender] = useState(
    Constants.genderOptions[0]
  );
  const [range, setRange] = useState({});
  const [showCalender, setShowCalender] = useState(false);
  const [initialDate, setInitialDate] = useState(initDate);
  const calendarRef = useRef(null);
  const viewPermission = usePermissions(Constants.permission.CUSTOMER_VIEW);
  const [loader, setLoader] = useState(false);
  const location = useLocation();

  dayjs.extend(utc);
  dayjs.extend(timezone);


  useEffect(() => {
    // Users list API
    getUsersData();
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Set user list data
    setUsersData(userslist.user);
  }, [userslist]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // De-bounce function for search with delay of 500 seconds
    if (search) {
      const delayDebounceFn = setTimeout(() => {
        getUsersData(search);
      }, 500);
      return () => {
        clearTimeout(delayDebounceFn);
      };
    }
  }, [search]);

  const handleClickOutside = (event) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      setShowCalender(false);
    }
  };

  // Columns for User list
  const columns = [
    {
      name: Constants.id,
      width: "4%",
      cell: (_row, i) =>
        pageIndex * Constants.limitCount - Constants.limitCount + i + 1,
    },
    {
      name: Constants.email,
      grow: 4,
      wrap: true,
      selector: (row) => (
        <Link to={viewPermission ? `${RoutesUrl.users}/${row?._id}` : ""}
          state={{ usersIndexPage: location.pathname }}


        >
          <span
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={row?.email}
          >
            {row?.email}
          </span>
        </Link>
      ),
    },
    {
      name: Constants.name,
      // width: '250px',
      grow: 3,

      selector: (row) => {
        const { userName, name, firstName, lastName } = row || {};
        const fullName = firstName && lastName ? `${firstName} ${lastName}` : null;
        return fullName ?? name ?? userName ?? Constants.na;
      }


    },
    {
      name: Constants.gender,
      grow: 2,
      wrap: false,
      selector: (row) => titleCase(row?.gender),
      sortable: true
    },
    {
      name: Constants.dob,
      grow: 2,
      wrap: false,
      selector: (row) =>
        row?.dob ? dayjs(row?.dob).format(Constants.dateFormat) : Constants.na,
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const dateA = new Date(rowA.dob);
        const dateB = new Date(rowB.dob);
        return dateA - dateB;
      },
    },
    {
      name: Constants.height,
      selector: (row) =>
        row?.heightType === Constants.heightTypeFt
          ? `${row.feet} ft ${row.inchs} in`
          : `${row.centimeters} cms`,
    },
    {
      name: Constants.weight,
      selector: (row) =>
        `${row.weight} ${row.weightType === Constants.weightTypeLbs ? "lbs" : "kgs"
        }`,
    },
    {
      name: Constants.deviceId,
      // width: '300px',
      grow: 4,
      wrap: true,
      selector: (row) => (
        <span
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title={row?.deviceId || Constants.na}
        >
          {/* <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy-icon lucide-copy"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg> */}
          {row?.deviceId || Constants.na}
        </span>
      ),
    },
    {
      name: Constants.deviceName,
      selector: (row) => (
        <span
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title={row?.deviceName || Constants.na}
        >
          {row?.deviceName || Constants.na}
        </span>
      ),
    },

    {
      name: "Date of Joining",
      grow: 2,
      wrap: false,
      selector: (row) => row?.createdAt ? dayjs(row?.createdAt).format(Constants.dateFormat) : Constants.na,
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const dateA = new Date(rowA.createdAt);
        const dateB = new Date(rowB.createdAt);
        return dateA - dateB;
      },


    },

    /* {
      name: Constants.deviceId,
      selector: (row) => row?.deviceId || Constants.na,
    },
    {
      name: Constants.deviceName,
      selector: (row) => row?.deviceName || Constants.na,
    }, */
  ];

  const getUsersData = (searchKey) => {
    // User list API based on all filters
    // const data = {
    //   search: searchKey,
    //   gender: selectedGender.value,
    //   start_date: range?.start_date,
    //   end_date: range?.end_date,
    // };


    const tz = dayjs.tz.guess();
    const startOfDayISO = range?.start_date ? dayjs.tz(range.start_date, tz).startOf("day").toISOString() : null;
    const endOfDayISO = range?.start_date ? dayjs.tz(range?.end_date, tz).endOf("day").toISOString() : null;

    const data2 = {
      search: searchKey,
      gender: selectedGender.value,
      start_date: startOfDayISO,
      end_date: endOfDayISO,
    };

    dispatch(usersList(data2));
  };

  const handleGender = (selected) => {
    setSelectedGender(selected);
    setPageIndex(1);
    // const data = {
    //   gender: selected.value,
    //   search,
    //   start_date: range?.start_date,
    //   end_date: range?.end_date,
    // };

    const tz = dayjs.tz.guess();

    // const startOfDayISO = dayjs.tz(range?.start_date, tz).startOf("day").toISOString();
    // const endOfDayISO = dayjs.tz(range?.end_date, tz).endOf("day").toISOString();

    const startOfDayISO = range?.start_date ? dayjs.tz(range.start_date, tz).startOf("day").toISOString() : null;
    const endOfDayISO = range?.start_date ? dayjs.tz(range?.end_date, tz).endOf("day").toISOString() : null;

    const data2 = {
      gender: selected.value,
      search,
      start_date: startOfDayISO,
      end_date: endOfDayISO,
    };


    dispatch(usersList(data2));
  };

  const onResetFilter = () => {
    setSelectedGender(Constants.genderOptions?.[0]);
    setRange({});
    setInitialDate(initDate);
    setSearch("");
    setPageIndex(1);
    dispatch(usersList());
  };

  // Pagination fuction
  const handleOffset = (newOff) => {
    // const data = {
    //   skip: newOff,
    //   limit: Constants.limitCount,
    //   search,
    //   gender: selectedGender.value,
    //   start_date: range?.start_date,
    //   end_date: range?.end_date,
    // };

    const tz = dayjs.tz.guess();
    const startOfDayISO = range?.start_date ? dayjs.tz(range.start_date, tz).startOf("day").toISOString() : null;
    const endOfDayISO = range?.start_date ? dayjs.tz(range?.end_date, tz).endOf("day").toISOString() : null;

    const data2 = {
      skip: newOff,
      limit: Constants.limitCount,
      search,
      gender: selectedGender.value,
      start_date: startOfDayISO,
      end_date: endOfDayISO,
    };


    dispatch(usersList(data2));
  };

  const onRangeChange = (range) => {
    setInitialDate({
      startDate: range?.[0]?.startDate,
      endDate: range?.[0]?.endDate,
      key: "selection",
    });
    setRange({
      start_date: dayjs(range?.[0]?.startDate).format("YYYY-MM-DD"),
      end_date: dayjs(range?.[0]?.endDate).format("YYYY-MM-DD"),
    });
  };

  // date range filter
  const onDateFilter = () => {
    setShowCalender(false);
    // const data = {
    //   skip: 0,
    //   limit: Constants.limitCount,
    //   search,
    //   gender: selectedGender.value,
    //   start_date: range?.start_date,
    //   end_date: range?.end_date,
    // };

    const tz = dayjs.tz.guess();
    const startOfDayISO = dayjs.tz(range?.start_date, tz).startOf("day").toISOString();
    const endOfDayISO = dayjs.tz(range?.end_date, tz).endOf("day").toISOString();

    const data2 = {
      skip: 0,
      limit: Constants.limitCount,
      search,
      gender: selectedGender.value,
      start_date: startOfDayISO,
      end_date: endOfDayISO,
    };

    dispatch(usersList(data2));
  };


  const onExportExcel = async () => {
    try {
      setLoader(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${ApiUrl.EXPORT_USER_LIST}`,
        {
          // baseURL: `${process.env.REACT_APP_API_URL}`,
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              Constants.accessToken
            )}`,
          },
        }
      );
      setLoader(false);
      // Create a Blob from the data and generate a download link
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });


      const url = window.URL.createObjectURL(blob);

      const disposition = response.headers["content-disposition"];
      let fileName = "UserList.xlsx";
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) fileName = match[1];
      }
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setLoader(false);
      console.error("Error downloading the Excel file:", error);
    }
  };

  return (
    <div className="container-xxl">
      <div className="mt-3">
        <PageHeader pagetitle={Constants.customerList} />
      </div>
      <div className="row mb-4 align-items-end position-relative justify-content-end">
        <div className="col-md-4">
          <div className="search-w pull-right space-calender2 position-relative">
            <div
              className={`calender-outer pos_inherit ${range?.start_date ? "calender-active" : ""
                }`}
            >
              <i
                style={{
                  cursor: "pointer",
                  fontSize: "25px",
                }}
                className="fa fa-calendar"
                aria-hidden="true"
                onClick={() => {
                  setShowCalender((prevState) => !prevState);
                }}
              ></i>
            </div>
            <div className="row">
              {showCalender && (
                <div ref={calendarRef} className="date-pos">
                  <div>
                    <DateRangePicker
                      initialDate={initialDate}
                      rangeSelected={(range) => {
                        onRangeChange(range);
                      }}
                    />
                  </div>
                  <div className="appy-btn">
                    <Button
                      className="text-uppercase fw-bold text-center mx-auto d-block"
                      onClick={() => {
                        onDateFilter();
                      }}
                    >
                      {Constants.apply}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <ReactSelect
            label={`${Constants.gender}`}
            required={false}
            options={Constants.genderOptions}
            onChange={(selected) => handleGender(selected)}
            value={selectedGender}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">{Constants.search}</label>
          <input
            className="px-2 filter-sec-trans"
            type="search"
            value={search}
            placeholder={Constants.searchByEmail}
            onChange={(event) => {
              setSearch(event.target.value);
              setPageIndex(1);
              if (event.target.value === "") {
                setSearch("");
                getUsersData("");
              }
            }}
          ></input>
        </div>
        <div className="col-md-2 reset-btn">
          <button
            className="btn btn-primary text-uppercase px-4  cross-reset p-right fw-bold w-100"
            onClick={() => onResetFilter()}
          >
            {Constants.reset}
          </button>
        </div>

        {loader ? (
          <div className="col-md-2">
            <img src={Spinner} alt="spinner" width="40px" height="40px" />
          </div>
        ) : (

          <div className="col-md-2">
            <div className="btn-hover">
              <Link
                onClick={() => {
                  onExportExcel();
                }}
                title=""
                className="btn btn-primary text-uppercase"
                style={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  justifyContent: "center"
                }}
              >
                <img
                  src={ExcelIcon}
                  alt="excel-icon"
                  className="img-excel"
                />
                <span>  {Constants.export} </span>
              </Link>

            </div>
          </div>
        )
        }

      </div>
      <TableList
        columns={columns}
        data={usersData}
        defaultSortFieldId
        highlightOnHover
        responsive={true}
      />
      {userslist?.totalCount > Constants.limitCount && (
        <ReactPagination
          itemsPerPage={Constants.limitCount}
          total={userslist?.totalCount}
          page={(target) => setPageIndex(target + 1)}
          newOffset={handleOffset}
          pageIndex={pageIndex - 1}
        />
      )}
    </div>
  );
};

export default UsersList;
